'use client'
import { memo, useEffect, useMemo, useState } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { AnimatePresence, motion } from 'framer-motion'
import { mq, designGrid, vw, getTransition, font26_24 } from '@/styles'
import { useDeviceType, useInView, useTranslator } from '@/hooks'

interface IBrandsSection {
  brands: any
}

const getLogos = (images) => images.map((img, idx) => {
  return (
    <AnimatePresence initial={false} key={idx}>
      <motion.article className='brand' key={img} variants={logoVariants} initial='initial' exit='exit' animate='animate'>
        <figure>
          <img src={img} alt='Brand logo'/>
        </figure>
      </motion.article>
    </AnimatePresence>
  )
})

const getNewLogoArray = (prevLogoArray, images) => {
  const missingLogoArray = images.filter(img => {
    return !prevLogoArray.includes(img)
  })

  const newArrayLogo = Math.floor(Math.random() * missingLogoArray.length)
  const oldArrayLogo = Math.floor(Math.random() * prevLogoArray.length)

  const updatedArray = [...prevLogoArray]
  updatedArray[oldArrayLogo] = missingLogoArray[newArrayLogo]

  return updatedArray
}

const transition = { ...getTransition(1) }
const wrapperVariants = {
  inactive: {
    opacity: 0,
    transition
  },
  active: {
    opacity: 1,
    transition: {
      ...getTransition(1),
      delay: 1.5
    }
  }
}

export const SectionBrands = memo<IBrandsSection>(({ brands }) => {
  const { t } = useTranslator()
  const { lang } = useParams()
  const images = brands.map(brand => brand.logo.url)

  const { isMobile } = useDeviceType()
  const initialArray = images.slice(0, isMobile ? 3 : 5);
  const [logoArray, changeLogoArray] = useState(initialArray)

  const { ref, inView } = useInView({
    rootMargin: '0px', // TOP  RIGHT
    threshold: 1,
    triggerOnce: true
  })

  const Logos = useMemo(() => getLogos(logoArray), [logoArray])

  useEffect(() => {
    if (images.length > initialArray.length) {
      const interval = setInterval(() => {
        changeLogoArray(prevLogoArray => getNewLogoArray(prevLogoArray, images));
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [Logos]);

  useEffect(() => {
    const initialArray = images.slice(0, !isMobile ? 5 : 3);
    changeLogoArray(initialArray)
  }, [isMobile])

  return lang !== 'en' && (
    <Section ref={ref}>
      <h3>{t('brand')}</h3>
      <BrandWrapper variants={wrapperVariants} animate={inView ? 'active' : 'inactive'} initial='inactive'>
        {Logos}
      </BrandWrapper>
    </Section>
  )
})


const Section = styled.section`
  margin-bottom: ${vw(100, 'mobile')};

  ${mq.greaterThan('tablet')} {
    ${designGrid({})}
    margin-bottom: ${vw(140, 'desktop')};
  }

  h3 {
    ${font26_24(true)}
    font-family: var(--font-basier-circle);
    font-weight: 700;
    grid-area: 1 / 1 / span 1 / span 6;
    margin-bottom: ${vw(28, 'mobile')};

    ${mq.greaterThan('tablet')} {
      ${font26_24(false)}
      grid-area: 1 / 1 / span 1 / span 12;
      margin-bottom: ${vw(40, 'desktop')};
    }
  }
`

// filter gen:
// https://codepen.io/sosuke/pen/Pjoqqp
const BrandWrapper = styled(motion.div)`
  grid-area: 1 / 1 / span 1 / span 6;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  height: ${vw(57, 'mobile')};
  margin: 0;

  article, figure, img { height: inherit; }

  article {
    padding: ${vw(5, 'mobile')} ${vw(15, 'mobile')};
  }

  img {
    object-fit: contain;
    filter: invert(17%) sepia(35%) saturate(2913%) hue-rotate(314deg) brightness(91%) contrast(88%);
  }

  ${mq.greaterThan('tablet')} {
    grid-area: 2 / 2 / span 1 / span 10;
    grid-template-columns: repeat(5, 1fr);
    grid-gap: 50px;
    height: ${vw(108, 'desktop')};

    article {
      padding: 0;
      margin: 0 ${vw(30, 'desktop')};
    }
  }
`

const logoVariants = {
  initial: {
    opacity: 0,
    transition: {
      ...getTransition(),
    }
  },
  animate: {
    opacity: 1,
    transition: {
      ...getTransition(),
      duration: .3,
    }
  },
  exit: {
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .3,
    }
  },
}
